import React, { useEffect, useState } from 'react';
import { InlineShareButtons } from 'sharethis-reactjs';
import { IconShare } from '../CustomIcons';
import { useLocation } from 'react-router-dom';

interface ShareThisProps {
    alignment?: 'left' | 'center' | 'right';
    color?: 'social' | 'white';
    enabled?: boolean;
    font_size?: number;
    labels?: "cta" | "counts" | null;
    language?: string;
    networks?: Array<'whatsapp' | 'linkedin' | 'messenger' | 'facebook' | 'twitter' | 'pinterest' | 'email' | 'tumblr' | 'sms'>;
    padding?: number;
    radius?: number;
    show_total?: boolean;
    size?: number;
    image?: string;
    description?: string;
    title?: string;
    feedId?: string;
    leagueUrl?: string;
}


const ShareThis: React.FC<ShareThisProps> = ({
    alignment = 'center',
    color = 'social',
    enabled = true,
    font_size = 16,
    labels = null,
    language = 'en',
    networks = ['whatsapp', 'facebook', 'twitter', 'sms'],
    padding = 12,
    radius = 4,
    show_total = false,
    size = 30,
    image,
    description,
    title,
    feedId,
    leagueUrl,
}) => {


    const [loaded, setLoaded] = useState(false);
    const propertyId = (window.location.hostname === "fantasyloser.com") ? "689d79d0e16fb4e918cd47f3" : "689d79b8e16fb4e918cd47f2";
    const [hideShare, setHideShare] = useState(true);
    const [shareUrl, setShareUrl] = useState('');
    const location = useLocation();

    const makeAbsoluteUrl = (loc?: { pathname?: string; search?: string; hash?: string }, feedId?: string) => {
        let pathname = loc?.pathname ?? window.location.pathname;
        const search = loc?.search ?? window.location.search;
        const hash = loc?.hash ?? window.location.hash;

        if (feedId) {     
            pathname = `/${leagueUrl}/feed/${feedId}`;        
        }

        return `${window.location.protocol}//${window.location.host}${pathname}${search}${hash}`;
    };

    // Update when react-router location changes (keeps existing behavior)
    useEffect(() => {
        const full = makeAbsoluteUrl(location as any, feedId);
        setShareUrl(prev => (prev === full ? prev : full));
    }, [location, feedId]);

    // Patch history methods once and listen for custom 'locationchange' event
    useEffect(() => {
        // handler to update url state when history changes externally
        const onLocationChange = () => {
            const full = makeAbsoluteUrl(undefined, feedId);
            setShareUrl(prev => (prev === full ? prev : full));
        };

        // Monkey-patch pushState/replaceState only once across the app
        if (!(window as any).__sharethis_history_patched) {
            const wrap = (type: 'pushState' | 'replaceState') => {
                const orig = (window.history as any)[type] as Function;
                (window.history as any)[type] = function (...args: any[]) {
                    const res = orig.apply(this, args);
                    window.dispatchEvent(new Event('locationchange'));
                    return res;
                };
            };
            wrap('pushState');
            wrap('replaceState');
            // popstate should also trigger our locationchange so single listener handles all cases
            window.addEventListener('popstate', () => window.dispatchEvent(new Event('locationchange')));

            (window as any).__sharethis_history_patched = true;
        }

        window.addEventListener('locationchange', onLocationChange);

    // initialize shareUrl immediately on mount (and when feedId changes)
    onLocationChange();

        return () => {
            window.removeEventListener('locationchange', onLocationChange);
        };
    }, [feedId]); // re-run when feedId changes so shareUrl reflects it

    useEffect(() => {
        // check if script already added
        if (document.querySelector(`script[data-sharethis]`)) {
            setLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://platform-api.sharethis.com/js/sharethis.js#property=${propertyId}&product=inline-share-buttons`;
        script.async = true;
        script.defer = true;
        script.setAttribute('data-sharethis', 'true'); // marker to avoid duplicate injection
        script.onload = () => setLoaded(true);

        document.head.appendChild(script);
    }, [propertyId]);

    if (!loaded) return null; // render nothing until script is ready

    return (
        <div className="shareThisBlk">
            <div className="shareBtn" onClick={() => setHideShare(!hideShare)}><IconShare /><span>Share</span></div>
            <div className={hideShare ? 'shareOptions hideShare' : 'shareOptions'}>
                <InlineShareButtons
                    key={shareUrl}
                    config={{
                        alignment: alignment,  // alignment of buttons (left, center, right)
                        color: color,      // set the color of buttons (social, white)
                        enabled: enabled,        // show/hide buttons (true, false)
                        font_size: font_size,        // font size for the buttons
                        labels: labels,        // button labels (cta, counts, null)
                        language: language,       // which language to use (see LANGUAGES)
                        networks: networks,          // which networks to include (see SHARING NETWORKS)
                        padding: padding,          // padding within buttons (INTEGER)
                        radius: radius,            // the corner radius on each button (INTEGER)
                        show_total: show_total,
                        size: size,             // the size of each button (INTEGER)
                        url: shareUrl,
                        image: image,  // (defaults to og:image or twitter:image)
                        description: description,       // (defaults to og:description or twitter:description)
                        title: title,

                        // OPTIONAL PARAMETERS

                        /*min_count: 10,                    // (threshold for total share count to be displayed)
                        url: 'https://www.sharethis.com', // (defaults to current url)
                        image: 'https://bit.ly/2CMhCMC',  // (defaults to og:image or twitter:image)
                        description: 'custom text',       // (defaults to og:description or twitter:description)
                        title: 'custom title',            // (defaults to og:title or twitter:title)
                        message: 'custom email text',     // (only for email sharing)
                        subject: 'custom email subject',  // (only for email sharing)
                        username: 'custom twitter handle' // (only for twitter sharing)
                        */
                    }}
                />
            </div>
        </div>
    );
};

export default ShareThis;